/* GrowthOS — Invoice record (view) page.
   Rebuilt to the shared `.gos-leadrec` / `gos-lr-*` detail shell (mirrors the Quote/
   Sales Order record + the other detail pages): back row, header card (code + status
   badge + subject + meta, owner pill, PDF + Edit + dots menu, chips), a horizontal
   tab bar, a read-only Overview (info cards + read-only Line Items + Terms/Description)
   and the real Activity(Timeline)/Notes/Attachments/Emails child tabs. Replaces the
   legacy left-nav scroll-spy that reused InvoiceForm in "view mode" — InvoiceForm is
   now a plain editable form.
   Real capabilities preserved (same APIs/gating as the legacy page): Download PDF
   (`getInvoicePdf`), Edit (locked for paid/void/cancelled unless System Manager),
   Send to Customer (`sendEmailForInvoicePdf`), and Mark as Paid (`updateInvoice`,
   themed modal with Payment Method / Date / Reference / Status), plus a Locked chip. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchAttachmentsAndAppendToModule,
  fetchDetailAndAppendToModule,
  fetchEmailAndAppendToModule,
  fetchInvoiceSalesOrderedItemAndAppendToModule,
  fetchNotesAndAppendToModule,
  singleRowAddUpdateInStore,
} from '../../../redux/actions';
import { getGenericMasterFilteredListByKey, getInvoiceById, getUserRole } from '../../../redux/selector';
import { isLoadingByKey } from '../../../shared/controls/methods/getLoadingState';
import apiServices from '../../../services/service';
import Loader from '../../../shared/component/Loader';
import Notes from '../../sharedChild/note/notes';
import Attachments from '../../sharedChild/attachment/attachments';
import Email from '../../sharedChild/email/emails';
import Timeline from '../../sharedChild/timeline';

const ICONS = {
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />,
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  paperclip: <path d="M21 8l-9 9a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  link: <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />,
  location: (<><path d="M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></>),
  template: <path d="M4 4h16a1 1 0 0 1 1 1v4H3V5a1 1 0 0 1 1-1ZM3 9h7v11H4a1 1 0 0 1-1-1V9ZM10 9h11v6H10zM10 15h11v4a1 1 0 0 1-1 1H10v-5Z" />,
  calendar: <path d="M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />,
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  deal: <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />,
  card: (<><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></>),
  dots: <path d="M12 5h.01M12 12h.01M12 19h.01" />,
  chevL: <path d="M15 18l-6-6 6-6" />,
  chevD: <path d="M6 9l6 6 6-6" />,
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
  download: <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />,
  invoice: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM9 13h6M9 17h6" />,
  lock: (<><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>),
  checkCircle: (<><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></>),
  x: <path d="M18 6 6 18M6 6l12 12" />,
  check: <path d="M20 6 9 17l-5-5" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const CURRENCY = process.env.REACT_APP_CURRENCY || '$';
const money = (n) => `${CURRENCY} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const val = (v) => (v === 0 || v ? v : null);
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};
const fmtDT = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// InvoiceStatus is admin-configurable master data — infer a badge tone by substring.
const statusTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('overdue') || s.includes('void') || s.includes('cancel')) return 'red';
  if (s.includes('paid') || s.includes('complet')) return 'green';
  if (s.includes('partial') || s.includes('pend') || s.includes('due')) return 'amber';
  if (s.includes('sent') || s.includes('issued')) return 'blue';
  if (s.includes('draft') || s.includes('open')) return 'gray';
  return 'gray';
};

// small gos Select (status picker inside the Mark-as-Paid modal)
const MiniSelect = ({ value, placeholder, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => setOpen((o) => !o)}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <Ic name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          <div className="gos-dd-list">
            {options.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {options.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <Ic name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);

const TextCard = ({ title, icon, text }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className={'gos-lr-textbody' + (text ? '' : ' muted')}>{text || 'No content provided.'}</div>
  </div>
);

export const ViewInvoice = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payData, setPayData] = useState({ paymentMethod: '', paymentDate: '', referenceNumber: '', status: '', statusId: '' });
  const [refreshView, setRefreshView] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'invoice');
  const [loading, setLoading] = useState(storeLoading);
  const invoiceStatus = useSelector(getGenericMasterFilteredListByKey('InvoiceStatus'));

  const searchParams = new URLSearchParams(location.search);
  const invoiceID = searchParams.get('id');

  const userRole = useSelector(getUserRole);
  const isSystemManager = (userRole || '') === 'System Manager';

  const [invoiceLocalData, setInvoiceLocalData] = useState({ ...(location.state || {}), type: 'view', from: 'view' });
  const invoiceStoreData = useSelector((state) => getInvoiceById(state, location.state?.id || invoiceID));

  useEffect(() => {
    setInvoiceLocalData((prev) => ({ ...prev, ...invoiceStoreData }));
  }, [invoiceStoreData]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const invoiceId = location.state?.id || invoiceID;
    if (invoiceId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'Invoice', id: invoiceId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'Invoice', id: invoiceId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'Invoice', id: invoiceId }));
      dispatch(fetchEmailAndAppendToModule({ moduleName: 'Invoice', id: invoiceId }));
      dispatch(fetchInvoiceSalesOrderedItemAndAppendToModule({ moduleName: 'Invoice', id: invoiceId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, invoiceLocalData?.id, refreshView]);

  const cd = invoiceLocalData || {};
  const status = cd.status;
  const items = useMemo(() => (Array.isArray(cd.invoiceDetails) ? cd.invoiceDetails : []), [cd.invoiceDetails]);
  const owner = cd.invoiceOwner;

  const isLocked = status && ['paid', 'void', 'cancelled'].includes(String(status).toLowerCase());
  const editDisabled = isLocked && !isSystemManager;
  const isPaid = String(status || '').toLowerCase() === 'paid';

  const handleEditInvoice = () => {
    navigate(`/sales/editInvoice?id=${cd?.id}`, { state: { ...cd, from: 'view' } });
  };

  const downloadPdf = async () => {
    setLoading(true);
    try {
      const cleanedData = {
        ...cd,
        invoiceDetails: cd.invoiceDetails?.map(({ createdDateTime, updatedDateTime, ...rest }) => rest) || [],
      };
      delete cleanedData.notesList;
      delete cleanedData.createdDateTime;
      delete cleanedData.updatedDateTime;
      const response = await apiServices.getInvoicePdf(cleanedData);
      if (response.status === 200 && response.data instanceof Blob) {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${cd?.code || 'Invoice'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download PDF', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('An unexpected error occurred while downloading PDF.', { position: 'top-right' });
    } finally {
      setLoading(false);
    }
  };

  const sendToCustomer = async () => {
    setMenuOpen(false);
    try {
      const cleanedData = {
        ...cd,
        invoiceDetails: cd.invoiceDetails?.map(({ createdDateTime, updatedDateTime, ...rest }) => rest) || [],
      };
      if (cleanedData.dueDate == null) delete cleanedData.dueDate;
      if (cleanedData.invoiceDate == null) delete cleanedData.invoiceDate;
      if (cleanedData.paymentDate == null) delete cleanedData.paymentDate;
      const response = await apiServices.sendEmailForInvoicePdf(cleanedData);
      if (response.status === 200 && String(response.data || '').toLowerCase() === 'email sent successfully') {
        toast.success(response.data, { position: 'top-right' });
      } else {
        toast.error('Failed to send email.', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error sending invoice email:', error);
      toast.error('An error occurred while sending email.', { position: 'top-right' });
    }
  };

  const openMarkPaid = () => {
    setMenuOpen(false);
    setPayData({
      paymentMethod: cd.paymentMethod || '',
      paymentDate: cd.paymentDate ? String(cd.paymentDate).substring(0, 10) : '',
      referenceNumber: cd.referenceNumber || '',
      status: cd.status || '',
      statusId: cd.statusId || '',
    });
    setPayOpen(true);
  };

  const submitMarkPaid = () => {
    setLoading(true);
    const payload = {
      ...cd,
      paymentMethod: payData.paymentMethod,
      paymentDate: payData.paymentDate,
      referenceNumber: payData.referenceNumber,
      status: payData.status,
      statusId: payData.statusId,
    };
    apiServices
      .updateInvoice(payload)
      .then((response) => {
        if (response.status === 200) {
          setPayOpen(false);
          dispatch(singleRowAddUpdateInStore({ moduleName: 'Invoice', moduleKey: 'invoice', id: cd.id, rowIndex: cd.rowIndex }));
          toast.success('Invoice updated successfully!', { position: 'top-right' });
          setRefreshView((p) => !p);
          setLoading(false);
        } else {
          setLoading(false);
          const errorMessages = [];
          for (const key in response.errors) {
            if (response.errors.hasOwnProperty(key)) errorMessages.push(...response.errors[key]);
          }
          errorMessages.forEach((m) => toast.error(m, { position: 'top-right' }));
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        toast.error('An unexpected error occurred.', { position: 'top-right' });
        setLoading(false);
      });
  };

  const chips = [
    { icon: 'deal', label: 'Grand Total ' + money(cd.grandTotal) },
    { icon: 'grid', label: items.length + ' line item' + (items.length !== 1 ? 's' : '') },
    owner && { icon: 'user', label: 'Owner ' + owner },
    cd.createdDateTime && { icon: 'clock', label: 'Created ' + fmtDT(cd.createdDateTime) },
    isLocked && { icon: 'lock', label: 'Locked', tone: 'blue' },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'activity', label: 'Activity', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: cd.notesList?.length },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: cd.attachments?.length },
    { id: 'emails', label: 'Emails', icon: 'mail' },
  ];

  const addrRows = (p) => [
    ['Street', val(cd[p + 'Street'])],
    ['City', val(cd[p + 'City'])],
    ['State', val(cd[p + 'State'])],
    ['Country', val(cd[p + 'Country'])],
    ['Code', val(cd[p + 'Code'])],
  ];

  const lineItems = (
    <div className="gos-lr-card">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="grid" size={16} /> Line Items
          <span className="gos-lr-count">{items.length}</span>
        </div>
      </div>
      <div className="gos-qf-items">
        <div className="gos-qf-items-main">
          {items.length === 0 ? (
            <div className="gos-qr-empty">No items added to this invoice.</div>
          ) : (
            <div className="gos-qf-items-scroll">
              <div className="gos-qf-grid gos-qr-lines">
                <div className="gos-qf-hrow">
                  {['#', 'Product', 'Description', 'Qty', 'List Price', 'Total'].map((h, i) => (
                    <div key={i} className={'gos-qf-hcell' + (['Qty', 'List Price', 'Total'].includes(h) ? ' gos-num' : '')}>{h}</div>
                  ))}
                </div>
                {items.map((it, i) => (
                  <div className="gos-qf-row" key={it.id || i}>
                    <div className="gos-qf-c gos-qf-sn">{i + 1}</div>
                    <div className="gos-qf-c" style={{ fontWeight: 550 }}>{it.productName || '—'}</div>
                    <div className="gos-qf-c" style={{ color: 'var(--gos-text-3)' }}>{it.description || '—'}</div>
                    <div className="gos-qf-c gos-num gos-qf-ro">{it.quantity ?? 0}</div>
                    <div className="gos-qf-c gos-num gos-qf-ro">{money(it.listPrice)}</div>
                    <div className="gos-qf-c gos-num gos-qf-total">{money(it.lineTotal ?? it.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="gos-qf-summary">
          <div className="gos-qf-summary-head">Invoice Summary</div>
          <div className="gos-qf-summary-body">
            <div className="gos-qf-sum-row"><span>Sub Total</span><span className="gos-qf-sum-val">{money(cd.subTotal)}</span></div>
            <div className="gos-qf-sum-row"><span>Discount</span><span className="gos-qf-sum-val">− {money(cd.discountTotal)}</span></div>
            <div className="gos-qf-sum-row"><span>Tax</span><span className="gos-qf-sum-val">+ {money(cd.taxTotal)}</span></div>
            <div className="gos-qf-sum-row"><span>Adjustment</span><span className="gos-qf-sum-val">{money(cd.adjustment)}</span></div>
            <div className="gos-qf-sum-row gos-qf-grand"><span>Grand Total</span><span className="gos-qf-sum-val">{money(cd.grandTotal)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );

  const overview = (
    <>
      <div className="gos-lr-grid2">
        <InfoCard
          title="Invoice Information"
          icon="note"
          rows={[
            ['Invoice Owner', val(owner)],
            ['Subject', val(cd.subject)],
            ['Status', status ? <span className={'gos-badge gos-b-' + statusTone(status)}><span className="gos-pip" />{status}</span> : null],
            ['Invoice Date', val(fmtDate(cd.invoiceDate))],
            ['Due Date', val(fmtDate(cd.dueDate))],
            ['Currency', val(cd.currencyPrice)],
          ]}
        />
        <InfoCard
          title="Association With"
          icon="link"
          rows={[
            ['Account Name', val(cd.accountName)],
            ['Sales Order', val(cd.salesOrder)],
            ['Quote Name', val(cd.quoteName)],
            ['Contact Name', val(cd.contactName)],
            ['Created Date', val(fmtDT(cd.createdDateTime))],
          ]}
        />
        <InfoCard
          title="Payment Details"
          icon="card"
          rows={[
            ['Payment Method', val(cd.paymentMethod)],
            ['Date of Payment', val(fmtDate(cd.paymentDate))],
            ['Reference Number', val(cd.referenceNumber)],
            ['Purchase Order', val(cd.purchaseOrder)],
          ]}
        />
        <InfoCard title="Billing Address" icon="location" rows={addrRows('billing')} />
        <InfoCard title="Shipping Address" icon="location" rows={addrRows('shipping')} />
      </div>
      <div style={{ marginTop: 16 }}>{lineItems}</div>
      <div className="gos-lr-grid2" style={{ marginTop: 16 }}>
        <TextCard title="Terms and Conditions" icon="template" text={cd.termsAndConditions} />
        <TextCard title="Description" icon="note" text={cd.descriptionInformation} />
      </div>
    </>
  );

  let tabBody = overview;
  if (activeTab === 'activity') tabBody = <Timeline localData={cd} module={'Invoice'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={cd} module={'Invoice'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={cd} module={'Invoice'} />;
  else if (activeTab === 'emails') tabBody = <Email localData={cd} module={'Invoice'} />;

  return (
    <>
      {loading && <Loader />}
      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/sales/invoice')}>
            <Ic name="chevL" size={15} /> Invoices
          </button>
          {cd.code ? (
            <div className="gos-lr-leadid">
              Invoice <span>{cd.code}</span>
            </div>
          ) : null}
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: 'var(--gos-accent-soft)', color: 'var(--gos-accent)' }}>
                <Ic name="invoice" size={22} />
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {cd.code || 'Invoice'}
                  {status ? (
                    <span className={'gos-badge gos-b-' + statusTone(status)}>
                      <span className="gos-pip" />
                      {status}
                    </span>
                  ) : null}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--gos-text)', marginTop: 3 }}>
                  {cd.subject || 'Untitled invoice'}
                </div>
                <div className="gos-lr-subline">
                  <Ic name="building" size={14} />
                  {cd.accountName || '—'}
                  {fmtDate(cd.dueDate) ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="calendar" size={14} />
                      Due {fmtDate(cd.dueDate)}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              {owner ? (
                <span className="gos-qr-owner">
                  <span className="gos-lr-ava" style={{ width: 24, height: 24, fontSize: 10.5, background: gosAvaColor(owner) }}>
                    {gosInitials(owner)}
                  </span>
                  {owner}
                </span>
              ) : null}
              <button type="button" className="gos-lr-ghost-btn" onClick={downloadPdf} disabled={loading}>
                <Ic name="download" size={16} /> PDF
              </button>
              <button type="button" className="gos-lr-contact-btn" onClick={handleEditInvoice} disabled={editDisabled} title={editDisabled ? 'This invoice is locked and cannot be edited' : 'Edit invoice'}>
                <Ic name="edit" size={16} /> Edit
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={openMarkPaid} disabled={isPaid} title={isPaid ? 'Already paid' : undefined}>
                      <Ic name="checkCircle" size={15} /> {isPaid ? 'Paid' : 'Mark as Paid'}
                    </button>
                    <button type="button" className="gos-lr-menu-item" onClick={sendToCustomer}>
                      <Ic name="mail" size={15} /> Send to Customer
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className={'gos-lr-chip' + (c.tone ? ' gos-lr-chip-' + c.tone : '')} key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {tabBody}
      </div>

      {/* Mark as Paid modal (real updateInvoice capability) */}
      {payOpen ? (
        <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setPayOpen(false); }}>
          <div className="gos-import-modal" style={{ maxWidth: 560 }}>
            <div className="gos-import-head">
              <div className="gos-import-head-l">
                <Ic name="checkCircle" size={18} />
                <div className="gos-import-title">Mark Invoice as Paid</div>
              </div>
              <button type="button" className="gos-import-close" onClick={() => setPayOpen(false)} aria-label="Close">
                <Ic name="x" size={16} />
              </button>
            </div>
            <div className="gos-import-body">
              <div className="gos-cl-sec-body">
                <div className="gos-cl-field">
                  <label className="gos-cl-label">Payment Method</label>
                  <input className="gos-cl-input" value={payData.paymentMethod} placeholder="Enter Payment Method" onChange={(e) => setPayData((p) => ({ ...p, paymentMethod: e.target.value }))} />
                </div>
                <div className="gos-cl-field">
                  <label className="gos-cl-label">Date of Payment</label>
                  <input type="date" className="gos-cl-input" value={payData.paymentDate} onChange={(e) => setPayData((p) => ({ ...p, paymentDate: e.target.value }))} />
                </div>
                <div className="gos-cl-field">
                  <label className="gos-cl-label">Reference Number</label>
                  <input className="gos-cl-input" value={payData.referenceNumber} placeholder="Enter Reference Number" onChange={(e) => setPayData((p) => ({ ...p, referenceNumber: e.target.value }))} />
                </div>
                <div className="gos-cl-field">
                  <label className="gos-cl-label">Status</label>
                  <MiniSelect
                    value={payData.status}
                    placeholder="Select Status"
                    options={(invoiceStatus || []).map((s) => ({ value: s.id, label: s.displayName }))}
                    onChange={(o) => setPayData((p) => ({ ...p, status: o.label, statusId: o.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="gos-import-foot end">
              <div className="gos-foot-actions">
                <button type="button" className="gos-btn gos-btn-ghost" onClick={() => setPayOpen(false)}>Cancel</button>
                <button type="button" className="gos-btn gos-btn-primary" onClick={submitMarkPaid} disabled={loading}>
                  <Ic name="check" size={16} /> Submit Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ViewInvoice;
